import { useCallback, useEffect, useRef, useState } from 'react'

import Env from '@/env/env'
import getJWTToken from '@/utils/getJWTToken'

export interface IReportEvent {
  type: string
  data?: Record<string, unknown>
  timestamp: string
}

export const useReportStream = (subjectId: string | null) => {
  const [events, setEvents] = useState<IReportEvent[]>([])
  const [lastEvent, setLastEvent] = useState<IReportEvent | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [isReportCompleted, setIsReportCompleted] = useState(false)
  const eventSourceRef = useRef<EventSource | null>(null)
  const reconnectCountRef = useRef(0)
  const seenIdsRef = useRef<Set<string>>(new Set())

  const connect = useCallback(() => {
    if (!subjectId) return

    if (eventSourceRef.current) {
      eventSourceRef.current.close()
    }

    const token = getJWTToken()
    if (!token) return

    const url = `${Env.REACT_APP_BASE_API_URL}/api/v1/subjects/${subjectId}/events?token=${token}`
    const eventSource = new EventSource(url)
    eventSourceRef.current = eventSource

    eventSource.onopen = () => {
      setIsConnected(true)
      reconnectCountRef.current = 0
    }

    eventSource.onmessage = (event) => {
      try {
        const parsed: IReportEvent = JSON.parse(event.data)
        const eventId = event.lastEventId

        setLastEvent(parsed)
        setEvents((prev) => {
          // On reconnect the server replays missed events using Last-Event-Id.
          // Track seen IDs to avoid duplicating events already in state.
          if (eventId) {
            if (seenIdsRef.current.has(eventId)) return prev
            seenIdsRef.current.add(eventId)
          }
          return [...prev, parsed]
        })

        // pdf:completed is the last event in the pipeline — it signals the whole report is done.
        if (parsed.type === 'pdf:completed') {
          setIsReportCompleted(true)
        }
      } catch {
        console.warn('[SSE] Failed to parse event:', event.data)
      }
    }

    eventSource.onerror = () => {
      if (eventSource.readyState === EventSource.CLOSED) {
        setIsConnected(false)
        eventSourceRef.current = null
      } else {
        setIsConnected(false)
        reconnectCountRef.current += 1

        if (reconnectCountRef.current > 10) {
          eventSource.close()
          eventSourceRef.current = null
        }
      }
    }
  }, [subjectId])

  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
      setIsConnected(false)
    }
  }, [])

  useEffect(() => {
    if (subjectId) {
      connect()
    }

    return () => {
      disconnect()
    }
  }, [subjectId, connect, disconnect])

  return { events, lastEvent, isConnected, isReportCompleted, disconnect }
}
